import { ConfigProvider } from "antd";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";

import type { ThemeMode } from "sandbox/types";

import { STORAGE_KEYS, SANDBOX_THEME_CSS_VAR_KEYS } from "sandbox/constants";
import { sandboxThemes } from "sandbox/themes";
import { useLocalStorage } from "shell/api/hooks";

interface ThemeModeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextType>({
  mode: "light",
  toggleMode: () => {},
});

export const ThemeModeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setMode] = useLocalStorage<ThemeMode>(
    STORAGE_KEYS.themeMode,
    "light",
  );

  const handleToggleMode = useCallback(() => {
    setMode(mode === "dark" ? "light" : "dark");
  }, [mode, setMode]);

  const value = useMemo(
    () => ({ mode, toggleMode: handleToggleMode }),
    [mode, handleToggleMode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <div
        className={SANDBOX_THEME_CSS_VAR_KEYS[mode]}
        style={{ color: "var(--ant-color-text)" }}
      >
        <ConfigProvider theme={sandboxThemes[mode]}>{children}</ConfigProvider>
      </div>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => useContext(ThemeModeContext);
